import * as React from 'react'
import ReactMarkdown, { type Components, defaultUrlTransform } from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import { Download, FolderOpen } from 'lucide-react'
import 'katex/dist/katex.min.css'
import { cn } from '../../lib/utils'
import { usePlatform, type DownloadAssetRequest } from '../../context/PlatformContext'
import { createImageDownloadRequest, downloadAssetViaAnchor } from '../../lib/download-asset'
import { CodeBlock, InlineCode } from './CodeBlock'
import { MarkdownDiffBlock } from './MarkdownDiffBlock'
import { MarkdownJsonBlock } from './MarkdownJsonBlock'
import { MarkdownMermaidBlock } from './MarkdownMermaidBlock'
import { MarkdownDatatableBlock } from './MarkdownDatatableBlock'
import { MarkdownSpreadsheetBlock } from './MarkdownSpreadsheetBlock'
import { MarkdownHtmlBlock } from './MarkdownHtmlBlock'
import { MarkdownImageBlock } from './MarkdownImageBlock'
import { MarkdownLatexBlock } from './MarkdownLatexBlock'
import { MarkdownPdfBlock } from './MarkdownPdfBlock'
import { MarkdownFileLinkIcon } from './file-link-icons'
import { preprocessLinks } from './linkify'
import { classifyMarkdownLinkTarget, getCodeBlockLineFileTarget, getInlineCodeFileTarget, getMarkdownFileTarget, normalizeMarkdownFileTarget } from './link-target'
import { resolveMarkdownLocalPathTarget } from './path-resolution'
import { getCachedDataUrl, loadCachedDataUrl } from '../../lib/data-url-cache'
import remarkCollapsibleSections from './remarkCollapsibleSections'
import { CollapsibleSection } from './CollapsibleSection'
import { useCollapsibleMarkdown } from './CollapsibleMarkdownContext'
import { wrapWithSafeProxy } from './safe-components'
import { MARKDOWN_MATH_OPTIONS } from './math-options'
import { normalizeMarkdownCompatibility } from './compatibility'

/**
 * URL transform that extends react-markdown's default to also allow data: URIs
 * and local file paths.
 * The default `defaultUrlTransform` only permits http(s), ircs, mailto, xmpp —
 * data: URLs (used by image-generating models like Gemini) and absolute file
 * paths (used by generated images saved to disk) are stripped without this.
 */
function urlTransformAllowData(url: string): string {
  if (url.startsWith('data:') || url.startsWith('#__dataimg_')) return url
  // Session-local artifact aliases are resolved by the click handler against
  // the active session folder. Keep them intact through react-markdown's URL
  // sanitization instead of treating `sandbox:` as an external scheme.
  if (/^sandbox:/i.test(url) || url.includes('{{SESSION_PATH}}')) return url
  // Allow absolute file paths (macOS/Linux: /path, Windows: C:\path) and file:// URLs
  // These are used by image generation tools that save images to local disk
  if (url.startsWith('/') || url.startsWith('file://') || /^[A-Za-z]:[/\\]/.test(url) || /^[A-Za-z]:%5[Cc]/.test(url)) return url
  return defaultUrlTransform(url)
}

/**
 * Extract large inline data-URL images from markdown text BEFORE passing
 * to preprocessLinks / react-markdown. This prevents the ~1MB base64 strings
 * from being scanned by linkify regexes and the markdown AST parser.
 *
 * Returns cleaned text with short placeholders and a map to resolve them.
 * Uses indexOf-based scanning to avoid running regex on megabyte-scale text.
 */
function extractDataUrlImages(text: string): { cleaned: string; imageMap: Map<string, string> } {
  const imageMap = new Map<string, string>()
  if (!text.includes('data:image/')) return { cleaned: text, imageMap }

  let result = ''
  let pos = 0
  let imgIdx = 0

  while (pos < text.length) {
    const imgStart = text.indexOf('![', pos)
    if (imgStart === -1) { result += text.slice(pos); break }

    const altEnd = text.indexOf('](', imgStart + 2)
    if (altEnd === -1) { result += text.slice(pos); break }

    const urlStart = altEnd + 2
    if (!text.startsWith('data:image/', urlStart)) {
      result += text.slice(pos, urlStart)
      pos = urlStart
      continue
    }

    // Find the closing ) — scan forward from urlStart
    const urlEnd = text.indexOf(')', urlStart)
    if (urlEnd === -1) { result += text.slice(pos); break }

    const alt = text.slice(imgStart + 2, altEnd)
    const dataUrl = text.slice(urlStart, urlEnd)
    const placeholder = `#__dataimg_${imgIdx++}__`
    imageMap.set(placeholder, dataUrl)

    result += text.slice(pos, imgStart) + `![${alt}](${placeholder})`
    pos = urlEnd + 1
  }

  return { cleaned: result, imageMap }
}

/**
 * Render modes for markdown content:
 *
 * - 'terminal': Raw output with minimal formatting, control chars visible
 *   Best for: Debug output, raw logs, when you want to see exactly what's there
 *
 * - 'minimal': Clean rendering with syntax highlighting but no extra chrome
 *   Best for: Chat messages, inline content, when you want readability without clutter
 *
 * - 'full': Rich rendering with beautiful tables, styled code blocks, proper typography
 *   Best for: Documentation, long-form content, when presentation matters
 */
export type RenderMode = 'terminal' | 'minimal' | 'full'

export interface MarkdownProps {
  children: string
  /**
   * Render mode controlling formatting level
   * @default 'minimal'
   */
  mode?: RenderMode
  className?: string
  /**
   * Message ID for memoization (optional)
   * When provided, memoizes parsed blocks to avoid re-parsing during streaming
   */
  id?: string
  /**
   * Callback when a URL is clicked
   */
  onUrlClick?: (url: string) => void
  /**
   * Callback when a file path is clicked
   */
  onFileClick?: (path: string) => void
  /**
   * Enable collapsible headings
   * Requires wrapping in CollapsibleMarkdownProvider
   * @default false
   */
  collapsible?: boolean
  /**
   * Hide expand button on first mermaid block (when message starts with mermaid)
   * Used in chat to avoid overlap with TurnCard's fullscreen button
   * @default true
   */
  hideFirstMermaidExpand?: boolean
  /**
   * Base path for resolving relative image paths (e.g., session directory).
   * When set, relative paths like "generated-images/foo.jpg" in ![](path) are
   * resolved to absolute paths like "{basePath}/generated-images/foo.jpg".
   */
  basePath?: string
  /**
   * Additional directories for resolving bare relative file names from tool output.
   * These are tried before basePath for names like "screenshot.png".
   */
  relativeFileBasePaths?: string[]
}

/** Context for collapsible sections */
interface CollapsibleContext {
  collapsedSections: Set<string>
  toggleSection: (id: string) => void
}

/**
 * InlineLocalImage - Renders a local file path image by loading it as a data URL.
 * Used when markdown contains ![alt](/local/path) referencing a file on disk.
 */
function InlineLocalImage({ src, alt, onReadFileDataUrl, onDownloadAsset, onRevealInFinder, ...props }: {
  src: string
  alt?: string
  onReadFileDataUrl: (path: string) => Promise<string>
  onDownloadAsset?: (request: DownloadAssetRequest) => Promise<void>
  onRevealInFinder?: (path: string) => void
  [key: string]: unknown
}) {
  const [dataUrl, setDataUrl] = React.useState<string | null>(() => getCachedDataUrl(src) ?? null)
  const [error, setError] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    setError(false)
    const cached = getCachedDataUrl(src)
    if (cached) {
      setDataUrl(cached)
      return () => { cancelled = true }
    }

    setDataUrl(null)
    loadCachedDataUrl(src, () => onReadFileDataUrl(src))
      .then((url) => { if (!cancelled) setDataUrl(url) })
      .catch(() => { if (!cancelled) setError(true) })
    return () => { cancelled = true }
  }, [src, onReadFileDataUrl])

  const handleDownload = React.useCallback(async () => {
    if (!dataUrl) return

    const request = createImageDownloadRequest(src, {
      renderedSrc: dataUrl,
      label: alt,
    })

    if (onDownloadAsset) {
      await onDownloadAsset(request)
      return
    }

    downloadAssetViaAnchor(request)
  }, [alt, dataUrl, onDownloadAsset, src])

  if (error) {
    return <span className="text-muted-foreground text-sm">[Image not found: {src.split('/').pop()}]</span>
  }

  if (!dataUrl) {
    return <span className="inline-block my-2 text-muted-foreground text-sm">Loading image...</span>
  }

  return (
    <span className="group/img relative inline-block my-2">
      <img
        src={dataUrl}
        alt={alt || ''}
        className="max-w-full rounded-lg"
        style={{ maxHeight: '512px' }}
        loading="lazy"
        {...props}
      />
      <span className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover/img:opacity-100 transition-opacity">
        {onRevealInFinder && (
          <button
            type="button"
            onClick={() => onRevealInFinder(src)}
            className="p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm transition-colors"
            title="Reveal in folder"
          >
            <FolderOpen className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            void handleDownload()
          }}
          className="p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm transition-colors"
          title="Download"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
      </span>
    </span>
  )
}

/**
 * Create custom components based on render mode.
 *
 * @param firstMermaidCodeRef - Ref holding the code of the first mermaid block
 *   when the markdown message starts with a mermaid fence. Used to hide the
 *   inline expand button on that block (TurnCard's own fullscreen button
 *   occupies the same top-right position). A ref is used so the closure can
 *   read the latest value without adding content to the memo deps — that would
 *   cause component re-mounting on every streaming update.
 * @param hideFirstMermaidExpand - Whether to hide the expand button on the first
 *   mermaid block when the message starts with a mermaid fence. Defaults to true.
 */
function stableHash(input: string): string {
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

function createComponents(
  mode: RenderMode,
  onUrlClick?: (url: string) => void,
  onFileClick?: (path: string) => void,
  collapsibleContext?: CollapsibleContext | null,
  firstMermaidCodeRef?: React.RefObject<string | null>,
  hideFirstMermaidExpand: boolean = true,
  dataUrlMapRef?: React.RefObject<Map<string, string>>,
  platformActions?: {
    onRevealInFinder?: (path: string) => void
    onReadFileDataUrl?: (path: string) => Promise<string>
    onDownloadAsset?: (request: DownloadAssetRequest) => Promise<void>
  },
  basePath?: string,
  relativeFileBasePaths?: string[],
): Partial<Components> {
  let blockIndex = 0
  const renderInlineCode = (children: React.ReactNode) => {
    const value = React.Children.toArray(children)
      .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
      .join('')
    const target = getInlineCodeFileTarget(value)

    if (target && onFileClick) {
      const resolvedTarget = resolveMarkdownLocalPathTarget(target, {
        basePath,
        relativeFileBasePaths,
      })

      return (
        <button
          type="button"
          className="inline cursor-pointer border-0 bg-transparent p-0 text-left align-baseline hover:underline"
          onClick={() => onFileClick(resolvedTarget)}
          title={resolvedTarget}
        >
          <MarkdownFileLinkIcon path={resolvedTarget} />
          <InlineCode>{target}</InlineCode>
        </button>
      )
    }

    return <InlineCode>{children}</InlineCode>
  }

  const renderPlainCodeBlock = (
    code: string,
    language: string | undefined,
    className: string | undefined,
    nodePosition?: { start?: { line?: number }; end?: { line?: number } },
  ) => {
    const allowBareFileName = Boolean(basePath || relativeFileBasePaths?.length)
    const lines = code.split('\n')
    const isPlainTextBlock = !language || /^(?:text|txt|plain|plaintext)$/i.test(language)
    const hasClickableLine = isPlainTextBlock
      && Boolean(onFileClick)
      && lines.some((line) => getCodeBlockLineFileTarget(line, { allowBareFileName }))

    if (!hasClickableLine || !onFileClick) {
      return wrapBlock('code', code, <CodeBlock code={code} language={language} mode="full" className={className} />, nodePosition)
    }

    return wrapBlock(
      'code',
      code,
      <pre className={cn('font-mono text-sm whitespace-pre-wrap break-all', className)}>
        {lines.map((line, index) => {
          const target = getCodeBlockLineFileTarget(line, { allowBareFileName })
          const suffix = index < lines.length - 1 ? '\n' : ''

          if (!target) {
            return <React.Fragment key={index}>{line}{suffix}</React.Fragment>
          }

          const resolvedTarget = resolveMarkdownLocalPathTarget(target, {
            basePath,
            relativeFileBasePaths,
          })

          return (
            <React.Fragment key={index}>
              <button
                type="button"
                className="inline cursor-pointer border-0 bg-transparent p-0 font-inherit text-left text-accent hover:underline"
                onClick={() => onFileClick(resolvedTarget)}
                title={resolvedTarget}
              >
                <MarkdownFileLinkIcon path={resolvedTarget} />
                {target}
              </button>
              {suffix}
            </React.Fragment>
          )
        })}
      </pre>,
      nodePosition,
    )
  }

  const wrapBlock = (
    blockType: string,
    content: string,
    child: React.ReactNode,
    nodePosition?: { start?: { line?: number }; end?: { line?: number } },
  ) => {
    blockIndex += 1
    const startLine = nodePosition?.start?.line
    const endLine = nodePosition?.end?.line
    const path = startLine && endLine
      ? `line:${startLine}-${endLine}`
      : `idx:${blockIndex}`
    const blockId = `blk-${stableHash(`${blockType}|${path}|${content.slice(0, 240)}`)}`

    return (
      <div
        data-ca-block-type={blockType}
        data-ca-block-path={path}
        data-ca-block-id={blockId}
      >
        {child}
      </div>
    )
  }

  const baseComponents: Partial<Components> = {
    // Section wrapper for collapsible headings
    div: ({ node, children, ...props }) => {
      const sectionId = (props as Record<string, unknown>)['data-section-id'] as string | undefined
      const headingLevel = (props as Record<string, unknown>)['data-heading-level'] as number | undefined

      // If this is a collapsible section div and we have context
      if (sectionId && headingLevel && collapsibleContext) {
        return (
          <CollapsibleSection
            sectionId={sectionId}
            headingLevel={headingLevel}
            isCollapsed={collapsibleContext.collapsedSections.has(sectionId)}
            onToggle={collapsibleContext.toggleSection}
          >
            {children}
          </CollapsibleSection>
        )
      }

      // Regular div
      return <div {...props}>{children}</div>
    },
    // Links: Make clickable with callbacks
    a: ({ href, children }) => {
      const fallbackText = React.Children.toArray(children)
        .map((child) => (typeof child === 'string' ? child : ''))
        .join('')
        .trim()
      const target = (href?.trim() || fallbackText)
      const targetType = target ? classifyMarkdownLinkTarget(target) : 'url'
      const title = targetType === 'file' ? normalizeMarkdownFileTarget(target) : undefined
      const fileTarget = targetType === 'file' ? getMarkdownFileTarget(target) : null
      const resolvedFileTarget = targetType === 'file'
        ? resolveMarkdownLocalPathTarget(fileTarget!, {
          basePath,
          relativeFileBasePaths,
        })
        : null
      const fallbackFileTarget = fallbackText && classifyMarkdownLinkTarget(fallbackText) === 'file'
        ? getMarkdownFileTarget(fallbackText)
        : null
      const displayChildren = fallbackFileTarget && fileTarget && fallbackFileTarget === fileTarget
        ? fallbackFileTarget
        : children

      const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()

        // Some AI outputs include raw HTML anchors with empty href but path text content.
        // Fallback to the anchor text when href is missing/empty.
        if (!target) return

        if (targetType === 'file' && onFileClick && resolvedFileTarget) {
          onFileClick(resolvedFileTarget)
        } else if (onUrlClick) {
          onUrlClick(target)
        }
      }

      return (
        <a
          href={href}
          title={title}
          onClick={handleClick}
          className="text-accent hover:underline cursor-pointer"
        >
          {resolvedFileTarget && <MarkdownFileLinkIcon path={resolvedFileTarget} />}
          {displayChildren}
        </a>
      )
    },
  }

  // Terminal mode: minimal formatting
  if (mode === 'terminal') {
    return {
      ...baseComponents,
      // No special code handling - just monospace
      code: ({ children }) => (
        <code className="font-mono">{children}</code>
      ),
      pre: ({ children }) => (
        <pre className="font-mono whitespace-pre-wrap my-2">{children}</pre>
      ),
      // Minimal paragraph spacing
      p: ({ children }) => <p className="my-1">{children}</p>,
      // Simple lists
      ul: ({ children }) => <ul className="list-disc list-inside my-1">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal list-inside my-1">{children}</ol>,
      li: ({ children }) => <li className="my-0.5">{children}</li>,
      // Plain tables
      table: ({ children }) => (
        <table className="my-2 font-mono text-sm">{children}</table>
      ),
      th: ({ children }) => <th className="text-left pr-4">{children}</th>,
      td: ({ children }) => <td className="pr-4">{children}</td>,
    }
  }

  // Minimal mode: clean with syntax highlighting
  if (mode === 'minimal') {
    return {
      ...baseComponents,
      // Inline code
      code: ({ className, children, ...props }) => {
        const match = /language-([\w-]+)/.exec(className || '')
        const isBlock = 'node' in props && props.node?.position?.start.line !== props.node?.position?.end.line

        // Block code
        if (match || isBlock) {
          const code = String(children).replace(/\n$/, '')
          // Diff code blocks → pierre/diffs for a proper diff viewer
          if (match?.[1] === 'diff') {
            return wrapBlock('code', code, <MarkdownDiffBlock code={code} className="my-2" />, props.node?.position)
          }
          // JSON code blocks → interactive tree viewer
          if (match?.[1] === 'json') {
            return wrapBlock('code', code, <MarkdownJsonBlock code={code} className="my-2" />, props.node?.position)
          }
          // Datatable code blocks → sortable/filterable data table
          if (match?.[1] === 'datatable') {
            return wrapBlock('datatable', code, <MarkdownDatatableBlock code={code} className="my-2" />, props.node?.position)
          }
          // Spreadsheet code blocks → Excel-style grid
          if (match?.[1] === 'spreadsheet') {
            return wrapBlock('spreadsheet', code, <MarkdownSpreadsheetBlock code={code} className="my-2" />, props.node?.position)
          }
          // HTML preview blocks → sandboxed iframe
          if (match?.[1] === 'html-preview') {
            return wrapBlock('html-preview', code, <MarkdownHtmlBlock code={code} className="my-2" />, props.node?.position)
          }
          // PDF preview blocks → inline first page with expand to full viewer
          if (match?.[1] === 'pdf-preview') {
            return wrapBlock('pdf-preview', code, <MarkdownPdfBlock code={code} className="my-2" />, props.node?.position)
          }
          // Image preview blocks → inline image with expand to full viewer
          if (match?.[1] === 'image-preview') {
            return wrapBlock('image-preview', code, (
              <MarkdownImageBlock
                code={code}
                className="my-2"
                basePath={basePath}
                relativeFileBasePaths={relativeFileBasePaths}
              />
            ), props.node?.position)
          }
          // LaTeX/math code blocks → KaTeX rendered display math
          if (match?.[1] === 'latex' || match?.[1] === 'math') {
            return wrapBlock('latex', code, <MarkdownLatexBlock code={code} className="my-2" />, props.node?.position)
          }
          // Mermaid code blocks → zinc-styled SVG diagram.
          // Hide the inline expand button when the mermaid block is the first
          // content in the message — TurnCard's own fullscreen button occupies
          // the same top-right spot. Detection uses firstMermaidCodeRef (content
          // match) rather than AST line positions which are unreliable after
          // preprocessLinks transforms the markdown.
          if (match?.[1] === 'mermaid') {
            const isFirstBlock = hideFirstMermaidExpand &&
                                firstMermaidCodeRef?.current != null &&
                                code === firstMermaidCodeRef.current
            return wrapBlock(
              'mermaid',
              code,
              <MarkdownMermaidBlock code={code} className="my-2" showExpandButton={!isFirstBlock} />,
              props.node?.position,
            )
          }
          return renderPlainCodeBlock(code, match?.[1], 'my-2', props.node?.position)
        }

        // Inline code
        return renderInlineCode(children)
      },
      pre: ({ children }) => <>{children}</>,
      // Comfortable paragraph spacing
      p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
      // Styled lists - ul uses tighter spacing, ol uses standard for number alignment
      ul: ({ children, className }) => (
        <ul
          className={cn(
            'my-2 space-y-1 ps-[16px] pe-2 list-disc marker:text-[var(--md-bullets)]',
            className?.includes('contains-task-list') && 'list-none ps-0 marker:content-none',
          )}
        >
          {children}
        </ul>
      ),
      ol: ({ children, className }) => (
        <ol className={cn('my-2 space-y-1 pl-6 list-decimal', className)}>{children}</ol>
      ),
      li: ({ children, className }) => (
        <li className={cn(className?.includes('task-list-item') && 'list-none')}>{children}</li>
      ),
      input: ({ type, checked }) => {
        if (type === 'checkbox') {
          return (
            <input
              type="checkbox"
              checked={checked}
              readOnly
              className="mr-2 rounded border-muted-foreground align-middle"
            />
          )
        }
        return <input type={type} />
      },
      // Clean tables
      table: ({ children }) => (
        <div className="my-3 overflow-x-auto">
          <table className="min-w-full text-sm">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="border-b">{children}</thead>,
      th: ({ children }) => (
        <th className="text-left py-2 px-3 font-semibold text-muted-foreground">{children}</th>
      ),
      td: ({ children }) => (
        <td className="py-2 px-3 border-b border-border/50">{children}</td>
      ),
      // Headings - H1/H2 same size, differentiated by weight
      h1: ({ children }) => <h1 className="font-sans text-[16px] font-bold mt-5 mb-3">{children}</h1>,
      h2: ({ children }) => <h2 className="font-sans text-[16px] font-semibold mt-4 mb-3">{children}</h2>,
      h3: ({ children }) => <h3 className="font-sans text-[15px] font-semibold mt-4 mb-2">{children}</h3>,
      // Blockquotes
      blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-muted-foreground/30 pl-3 my-2 text-muted-foreground italic">
          {children}
        </blockquote>
      ),
      // Horizontal rules
      hr: () => <hr className="my-4 border-border" />,
      // Strong/emphasis
      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
    }
  }

  // Full mode: rich styling
  return {
    ...baseComponents,
    // Full code blocks with copy button
    code: ({ className, children, ...props }) => {
      const match = /language-([\w-]+)/.exec(className || '')
      const isBlock = 'node' in props && props.node?.position?.start.line !== props.node?.position?.end.line

      if (match || isBlock) {
        const code = String(children).replace(/\n$/, '')
        // Diff code blocks → pierre/diffs for a proper diff viewer
        if (match?.[1] === 'diff') {
          return wrapBlock('code', code, <MarkdownDiffBlock code={code} className="my-2" />, props.node?.position)
        }
        // JSON code blocks → interactive tree viewer
        if (match?.[1] === 'json') {
          return wrapBlock('code', code, <MarkdownJsonBlock code={code} className="my-2" />, props.node?.position)
        }
        // Datatable code blocks → sortable/filterable data table
        if (match?.[1] === 'datatable') {
          return wrapBlock('datatable', code, <MarkdownDatatableBlock code={code} className="my-2" />, props.node?.position)
        }
        // Spreadsheet code blocks → Excel-style grid
        if (match?.[1] === 'spreadsheet') {
          return wrapBlock('spreadsheet', code, <MarkdownSpreadsheetBlock code={code} className="my-2" />, props.node?.position)
        }
        // HTML preview blocks → sandboxed iframe
        if (match?.[1] === 'html-preview') {
          return wrapBlock('html-preview', code, <MarkdownHtmlBlock code={code} className="my-2" />, props.node?.position)
        }
        // PDF preview blocks → inline first page with expand to full viewer
        if (match?.[1] === 'pdf-preview') {
          return wrapBlock('pdf-preview', code, <MarkdownPdfBlock code={code} className="my-2" />, props.node?.position)
        }
        // Image preview blocks → inline image with expand to full viewer
        if (match?.[1] === 'image-preview') {
          return wrapBlock('image-preview', code, (
            <MarkdownImageBlock
              code={code}
              className="my-2"
              basePath={basePath}
              relativeFileBasePaths={relativeFileBasePaths}
            />
          ), props.node?.position)
        }
        // LaTeX/math code blocks → KaTeX rendered display math
        if (match?.[1] === 'latex' || match?.[1] === 'math') {
          return wrapBlock('latex', code, <MarkdownLatexBlock code={code} className="my-2" />, props.node?.position)
        }
        // Mermaid code blocks → zinc-styled SVG diagram.
        // (Same first-block detection as minimal mode — see comment above.)
        if (match?.[1] === 'mermaid') {
          const isFirstBlock = hideFirstMermaidExpand &&
                              firstMermaidCodeRef?.current != null &&
                              code === firstMermaidCodeRef.current
          return wrapBlock(
            'mermaid',
            code,
            <MarkdownMermaidBlock code={code} className="my-2" showExpandButton={!isFirstBlock} />,
            props.node?.position,
          )
        }
        return renderPlainCodeBlock(code, match?.[1], 'my-2', props.node?.position)
      }

      return renderInlineCode(children)
    },
    pre: ({ children }) => <>{children}</>,
    // Rich paragraph spacing
    p: ({ children }) => <p className="my-3 leading-relaxed">{children}</p>,
    // Styled lists - ul uses tighter spacing, ol uses standard for number alignment
    ul: ({ children, className }) => (
      <ul
        className={cn(
          'my-3 space-y-1.5 ps-[16px] pe-2 list-disc marker:text-[var(--md-bullets)]',
          className?.includes('contains-task-list') && 'list-none ps-0 marker:content-none',
        )}
      >
        {children}
      </ul>
    ),
    ol: ({ children, className }) => (
      <ol className={cn('my-3 space-y-1.5 pl-6 list-decimal', className)}>{children}</ol>
    ),
    li: ({ children, className }) => (
      <li className={cn('leading-relaxed', className?.includes('task-list-item') && 'list-none')}>{children}</li>
    ),
    // Beautiful tables
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto rounded-md border">
        <table className="min-w-full divide-y divide-border">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
    tbody: ({ children }) => <tbody className="divide-y divide-border">{children}</tbody>,
    th: ({ children }) => (
      <th className="text-left py-3 px-4 font-semibold text-sm">{children}</th>
    ),
    td: ({ children }) => (
      <td className="py-3 px-4 text-sm">{children}</td>
    ),
    tr: ({ children }) => (
      <tr className="hover:bg-muted/30 transition-colors">{children}</tr>
    ),
    // Rich headings - H1/H2 same size, differentiated by weight
    h1: ({ children }) => (
      <h1 className="font-sans text-[16px] font-bold mt-7 mb-4">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="font-sans text-[16px] font-semibold mt-6 mb-3">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="font-sans text-[15px] font-semibold mt-5 mb-3">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-[14px] font-semibold mt-3 mb-1">{children}</h4>
    ),
    // Styled blockquotes
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-foreground/30 bg-muted/30 pl-4 pr-3 py-2 my-3 rounded-r-md">
        {children}
      </blockquote>
    ),
    // Task lists (GFM)
    input: ({ type, checked }) => {
      if (type === 'checkbox') {
        return (
          <input
            type="checkbox"
            checked={checked}
            readOnly
            className="mr-2 rounded border-muted-foreground"
          />
        )
      }
      return <input type={type} />
    },
    // Horizontal rules
    hr: () => <hr className="my-6 border-border" />,
    // Strong/emphasis
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    del: ({ children }) => <del className="line-through text-muted-foreground">{children}</del>,
    // Inline images (including data: URIs from image-generating models and local file paths)
    // Resolves placeholders from extractDataUrlImages back to real data URLs.
    // Converts local file paths to data URLs via onReadFileDataUrl.
    // Shows download + reveal-in-folder buttons on hover.
    img: ({ src, alt, ...props }) => {
      // Resolve data URL placeholder if needed
      let resolvedSrc = (src && dataUrlMapRef?.current?.get(src)) || src || ''
      resolvedSrc = resolveMarkdownLocalPathTarget(resolvedSrc, {
        basePath,
        relativeFileBasePaths,
      })

      // Resolve relative paths against basePath (session directory) if available
      if (basePath && resolvedSrc.length > 0
        && !resolvedSrc.startsWith('data:')
        && !resolvedSrc.startsWith('http:')
        && !resolvedSrc.startsWith('https:')
        && !resolvedSrc.startsWith('#__dataimg_')
        && !resolvedSrc.startsWith('/')
        && !resolvedSrc.startsWith('file://')
        && !/^[A-Za-z]:[/\\]/.test(resolvedSrc)
      ) {
        // Relative path like "generated-images/xxx.jpg" → resolve to absolute
        resolvedSrc = basePath.endsWith('/') ? basePath + resolvedSrc : basePath + '/' + resolvedSrc
      }

      // Check if this is a local file path that needs to be loaded via IPC
      const isLocalFile = resolvedSrc.length > 0
        && !resolvedSrc.startsWith('data:')
        && !resolvedSrc.startsWith('http:')
        && !resolvedSrc.startsWith('https:')
        && !resolvedSrc.startsWith('#__dataimg_')
        && (resolvedSrc.startsWith('/') || resolvedSrc.startsWith('file://') || /^[A-Za-z]:[/\\]/.test(resolvedSrc))

      if (isLocalFile && platformActions?.onReadFileDataUrl) {
        return (
          <InlineLocalImage
            src={resolvedSrc}
            alt={alt}
            onReadFileDataUrl={platformActions.onReadFileDataUrl}
            onDownloadAsset={platformActions.onDownloadAsset}
            onRevealInFinder={platformActions?.onRevealInFinder}
            {...props}
          />
        )
      }

      const handleDownload = async () => {
        const request = createImageDownloadRequest(resolvedSrc, {
          label: alt,
        })

        if (platformActions?.onDownloadAsset) {
          await platformActions.onDownloadAsset(request)
          return
        }

        downloadAssetViaAnchor(request)
      }

      return (
        <span className="group/img relative inline-block my-2">
          <img
            src={resolvedSrc}
            alt={alt || ''}
            className="max-w-full rounded-lg"
            style={{ maxHeight: '512px' }}
            loading="lazy"
            {...props}
          />
          <span className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover/img:opacity-100 transition-opacity">
            {isLocalFile && platformActions?.onRevealInFinder && src && !src.startsWith('data:') && !src.startsWith('#__dataimg_') && (
              <button
                type="button"
                onClick={() => platformActions.onRevealInFinder!(src!)}
                className="p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm transition-colors"
                title="Reveal in folder"
              >
                <FolderOpen className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                void handleDownload()
              }}
              className="p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm transition-colors"
              title="Download"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          </span>
        </span>
      )
    },
    // Handle unknown <markdown> tags that may come through rehype-raw
    // Type assertion needed because 'markdown' is not a standard HTML element
    markdown: ({ children }: { children: React.ReactNode }) => <>{children}</>,
  } as Partial<Components>
}

/**
 * Markdown - Customizable markdown renderer with multiple render modes
 *
 * Features:
 * - Three render modes: terminal, minimal, full
 * - Syntax highlighting via Shiki
 * - GFM support (tables, task lists, strikethrough)
 * - Clickable links and file paths
 * - Memoization for streaming performance
 */
export function Markdown({
  children,
  mode = 'minimal',
  className,
  id,
  onUrlClick,
  onFileClick,
  collapsible = false,
  hideFirstMermaidExpand = true,
  basePath,
  relativeFileBasePaths,
}: MarkdownProps) {
  // Get collapsible context if enabled
  const collapsibleContext = useCollapsibleMarkdown()

  // Platform actions for image overlay buttons and local file loading
  const { onRevealInFinder, onReadFileDataUrl, onDownloadAsset } = usePlatform()

  // Extract the first mermaid code block's content when the message starts
  // with a mermaid fence. Stored in a ref so createComponents can read it
  // without adding `children` to the memo deps (which would remount all
  // components on every streaming update, breaking internal state).
  const firstMermaidCodeRef = React.useRef<string | null>(null)
  const trimmed = children.trimStart()
  if (trimmed.startsWith('```mermaid')) {
    const m = trimmed.match(/^```mermaid\n([\s\S]*?)```/)
    firstMermaidCodeRef.current = m?.[1] ? m[1].replace(/\n$/, '') : null
  } else {
    firstMermaidCodeRef.current = null
  }

  // Extract large data-URL images BEFORE linkify/markdown parsing for performance.
  // Stores data URLs in a ref-map; createComponents resolves them in the img handler.
  const dataUrlMapRef = React.useRef(new Map<string, string>())
  const processedContent = React.useMemo(() => {
    const { cleaned, imageMap } = extractDataUrlImages(children)
    dataUrlMapRef.current = imageMap
    return preprocessLinks(normalizeMarkdownCompatibility(cleaned))
  }, [children])

  const platformActions = React.useMemo(
    () => ({ onRevealInFinder, onReadFileDataUrl, onDownloadAsset }),
    [onDownloadAsset, onReadFileDataUrl, onRevealInFinder]
  )

  const components = React.useMemo(
    () => wrapWithSafeProxy(createComponents(mode, onUrlClick, onFileClick, collapsible ? collapsibleContext : null, firstMermaidCodeRef, hideFirstMermaidExpand, dataUrlMapRef, platformActions, basePath, relativeFileBasePaths)),
    [mode, onUrlClick, onFileClick, collapsible, collapsibleContext, hideFirstMermaidExpand, platformActions, basePath, relativeFileBasePaths]
  )

  // Conditionally include the collapsible sections plugin.
  // IMPORTANT: Disable single-dollar inline math so currency like $2M–$4M
  // stays plain text. Math should use $$...$$ delimiters.
  const remarkPlugins = React.useMemo(
    () => {
      const mathPlugin: [typeof remarkMath, typeof MARKDOWN_MATH_OPTIONS] = [
        remarkMath,
        MARKDOWN_MATH_OPTIONS
      ]
      return collapsible
        ? [remarkGfm, mathPlugin, remarkCollapsibleSections]
        : [remarkGfm, mathPlugin]
    },
    [collapsible]
  )

  return (
    <div className={cn('markdown-content', className)}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={[rehypeKatex, rehypeRaw]}
        urlTransform={urlTransformAllowData}
        components={components}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}

/**
 * MemoizedMarkdown - Optimized for streaming scenarios
 *
 * Splits content into blocks and memoizes each block separately,
 * so only new/changed blocks re-render during streaming.
 */
export const MemoizedMarkdown = React.memo(
  Markdown,
  (prevProps, nextProps) => {
    // If id is provided, use it for memoization
    if (prevProps.id && nextProps.id) {
      return (
        prevProps.id === nextProps.id &&
        prevProps.children === nextProps.children &&
        prevProps.mode === nextProps.mode
      )
    }
    // Otherwise compare content and mode
    return (
      prevProps.children === nextProps.children &&
      prevProps.mode === nextProps.mode
    )
  }
)
MemoizedMarkdown.displayName = 'MemoizedMarkdown'

// Re-export for convenience
export { CodeBlock, InlineCode } from './CodeBlock'
export { CollapsibleMarkdownProvider } from './CollapsibleMarkdownContext'
